import { motion } from "framer-motion";
import type { MenuItem } from "@/data/menu";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

function getMegaMenuWidth(item: MenuItem) {
  const columnCount = Math.min(item.columns.length, 5);
  return columnCount <= 1 ? 340 : columnCount === 2 ? 560 : columnCount === 3 ? 840 : 1080;
}

function MenuLabel({ label }: { label: string }) {
  return (
    <span className="flex items-center max-w-full">
      <span className="truncate" title={label}>{label}</span>
      <ArrowUpRight className="opacity-0 group-hover:opacity-100 ml-1 w-3.5 h-3.5 text-brand transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 duration-200 shrink-0" />
    </span>
  );
}

export function MegaMenu({
  item,
  left,
  activePath,
  onItemClick,
}: {
  item: MenuItem;
  left?: number;
  activePath?: string;
  onItemClick?: () => void;
}) {
  const columnCount = item.key === "case-studies"
    ? Math.min(item.columns.length, 4)
    : Math.min(item.columns.length, 3);
  const menuWidth = getMegaMenuWidth(item);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      className="top-full z-40 absolute bg-white dark:bg-background shadow-[0_30px_80px_-30px_rgba(0,0,0,0.25)] mt-[-8px] border border-border/60 rounded-2xl max-w-[calc(100vw-32px)]"
      style={{
        left: left !== undefined ? `clamp(16px, ${left}px, calc(100vw - min(${menuWidth}px, calc(100vw - 32px)) - 16px))` : "50%",
        transform: left === undefined ? "translateX(-50%)" : undefined,
        width: `min(${menuWidth}px, calc(100vw - 32px))`,
      }}
    >
      <div
        className="gap-6 lg:gap-8 grid p-6 lg:p-8"
        style={{
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        }}
      >
        {item.columns.map((col, ci) => (
          <div key={col.heading}>
            <motion.h3
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + ci * 0.04 }}
              className="mb-4 font-semibold text-brand text-xs uppercase tracking-[0.18em]"
            >
              {col.heading}
            </motion.h3>
            <ul className="space-y-3">
              {col.items.map((it, i) => {
                const isString = typeof it === "string";
                const label = isString ? it : it.label;
                const href = isString ? "#" : (it.path ?? "#");
                const isInternal =
                  !isString && typeof it.path === "string" && it.path.startsWith("/");
                const isActiveSubmenu = activePath === href;
                return (
                  <motion.li
                    key={`${col.heading}-${i}-${label}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 + ci * 0.04 + i * 0.02 }}
                  >
                    {isInternal ? (
                      <Link
                        to={href}
                        onClick={onItemClick}
                        className={`group block max-w-full text-[14px] transition-colors ${isActiveSubmenu ? "text-brand font-semibold" : "text-foreground/85 hover:text-brand"
                          }`}
                      >
                        <MenuLabel label={label} />
                      </Link>
                    ) : (
                      <a
                        href={href}
                        onClick={onItemClick}
                        className={`group block max-w-full text-[14px] transition-colors ${isActiveSubmenu ? "text-brand font-semibold" : "text-foreground/85 hover:text-brand"
                          }`}
                      >
                        <MenuLabel label={label} />
                      </a>
                    )}
                  </motion.li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
